import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Loader2,
  ChevronRight,
  ChevronDown,
  FolderOpen,
  Trash2,
  X as XIcon,
  MessageCircle,
  Pencil,
  Check,
  CheckCircle2,
  Inbox,
} from 'lucide-react'
import {
  listThemes,
  listFeedback,
  getThemeDetail,
  deleteTheme,
  updateTheme,
  removeThemeItems,
  generateAutoThemes,
  type FeedbackTheme,
  type FeedbackThemeDetail,
  type FeedbackItem,
  type AutoThemeProposal,
} from '../../lib/feedbackApi'
import { formatRelativeTime } from '../../lib/format'
import { FeedbackVoteButton } from './FeedbackVoteButton'
import { useAutoRefresh } from '../../hooks/useAutoRefresh'
import type { FeedbackFilterState } from './FeedbackFilters'
import { AutoThemeReview } from './AutoThemeReview'

const TYPE_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  bug: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: 'Bug' },
  feature: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', label: 'Feature' },
  improvement: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', label: 'Improvement' },
  integration: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', label: 'Integration' },
}

const STATUS_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  backlog: { bg: 'bg-secondary', text: 'text-muted-foreground', label: 'Backlog' },
  in_progress: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', label: 'In Progress' },
  done: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', label: 'Done' },
  dismissed: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: 'Dismissed' },
}

interface FeedbackThemesProps {
  isAdmin: boolean
  onItemSelect: (id: number) => void
  onThemeSelect?: (id: number) => void
  refreshKey: number
  mergeResult?: {
    mergedItem: FeedbackItem
    sourceIds: number[]
    nonce: number
  } | null
  filters?: FeedbackFilterState
  showArchive?: boolean
  onVoteChange?: (id: number, hasVoted: boolean, voteCount: number) => void
  autoGroupTrigger?: number
  onAutoGroupInfo?: (info: { generating: boolean; ungroupedCount: number }) => void
}

interface ThemeWithItems extends FeedbackTheme {
  items?: FeedbackItem[]
  expanded?: boolean
  loadingItems?: boolean
}

export function FeedbackThemes({
  isAdmin,
  onItemSelect,
  refreshKey,
  mergeResult,
  filters,
  showArchive,
  onVoteChange,
  onThemeSelect,
  autoGroupTrigger,
  onAutoGroupInfo,
}: FeedbackThemesProps) {
  const [themes, setThemes] = useState<ThemeWithItems[]>([])
  const [ungrouped, setUngrouped] = useState<FeedbackItem[]>([])
  const [loading, setLoading] = useState(true)
  const [ungroupedExpanded, setUngroupedExpanded] = useState(true)
  const [editingTheme, setEditingTheme] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [autoThemeProposals, setAutoThemeProposals] = useState<AutoThemeProposal[] | null>(null)
  const [autoThemeUnclassifiable, setAutoThemeUnclassifiable] = useState<number[]>([])
  const [autoThemeGenerating, setAutoThemeGenerating] = useState(false)
  const [autoThemeError, setAutoThemeError] = useState<string | null>(null)

  const filtersRef = useRef(filters)
  const showArchiveRef = useRef(showArchive)
  filtersRef.current = filters
  showArchiveRef.current = showArchive

  const fetchData = useCallback(async () => {
    const f = filtersRef.current
    const params: Record<string, unknown> = { per_page: 100 }
    if (f?.type) params.type = f.type
    if (f?.status) params.status = f.status
    if (f?.mine) params.mine = true
    if (f?.search) params.search = f.search
    if (f?.sort) params.sort = f.sort
    if (showArchiveRef.current) params.include_archived = true

    try {
      const [themesList, allFeedback] = await Promise.all([
        listThemes(),
        listFeedback(params),
      ])

      // Get theme details to know which items are grouped
      const details = await Promise.all(
        themesList.map((t) => getThemeDetail(t.id))
      )

      // Build set of all grouped feedback IDs
      const groupedIds = new Set<number>()
      const themesWithItems: ThemeWithItems[] = themesList.map((theme, i) => {
        const detail = details[i] as FeedbackThemeDetail
        detail.items.forEach((item) => groupedIds.add(item.id))
        return {
          ...theme,
          items: detail.items,
          expanded: true,
          loadingItems: false,
        }
      })

      // Items not in any theme
      const ungroupedItems = allFeedback.items.filter(
        (item) => !groupedIds.has(item.id)
      )

      setThemes(themesWithItems)
      setUngrouped(ungroupedItems)
      setLoading(false)
    } catch (err) {
      setLoading(false)
      throw err // re-throw for useAutoRefresh backoff
    }
  }, [])

  const fetchRef = useRef(fetchData)
  fetchRef.current = fetchData

  useEffect(() => {
    setLoading(true)
    fetchRef.current().catch(() => setLoading(false))
  }, [refreshKey, filters, showArchive])

  useEffect(() => {
    if (!mergeResult) return
    const sourceSet = new Set(mergeResult.sourceIds)

    setThemes((prev) =>
      prev.map((theme) => ({
        ...theme,
        items: theme.items
          ?.filter((item) => !sourceSet.has(item.id))
          .map((item) => (item.id === mergeResult.mergedItem.id ? mergeResult.mergedItem : item)),
      }))
    )
    setUngrouped((prev) =>
      prev
        .filter((item) => !sourceSet.has(item.id))
        .map((item) => (item.id === mergeResult.mergedItem.id ? mergeResult.mergedItem : item))
    )
  }, [mergeResult])

  useAutoRefresh(fetchData, { baseInterval: 60000, maxInterval: 180000 })

  // Report auto-group state to parent for the filter-row button
  useEffect(() => {
    onAutoGroupInfo?.({ generating: autoThemeGenerating, ungroupedCount: ungrouped.length })
  }, [autoThemeGenerating, ungrouped.length, onAutoGroupInfo])

  // React to parent's auto-group trigger
  const prevTrigger = useRef(autoGroupTrigger)
  useEffect(() => {
    if (autoGroupTrigger !== undefined && autoGroupTrigger > 0 && autoGroupTrigger !== prevTrigger.current) {
      prevTrigger.current = autoGroupTrigger
      handleAutoGroup()
    }
  }, [autoGroupTrigger])

  function toggleTheme(themeId: number) {
    setThemes((prev) =>
      prev.map((t) =>
        t.id === themeId ? { ...t, expanded: !t.expanded } : t
      )
    )
  }

  async function handleDeleteTheme(themeId: number) {
    try {
      await deleteTheme(themeId)
      fetchRef.current()
    } catch (err) {
      setAutoThemeError(err instanceof Error ? err.message : 'Failed to delete theme')
    }
  }

  async function handleRemoveFromTheme(themeId: number, feedbackId: number) {
    try {
      await removeThemeItems(themeId, [feedbackId])
      fetchRef.current()
    } catch (err) {
      setAutoThemeError(err instanceof Error ? err.message : 'Failed to remove item from theme')
    }
  }

  function startEditTheme(theme: ThemeWithItems) {
    setEditingTheme(theme.id)
    setEditName(theme.name)
    setEditDesc(theme.description || '')
  }

  async function saveEditTheme(themeId: number) {
    try {
      await updateTheme(themeId, {
        name: editName.trim(),
        description: editDesc.trim() || null,
      })
      setEditingTheme(null)
      fetchRef.current()
    } catch (err) {
      setAutoThemeError(err instanceof Error ? err.message : 'Failed to save theme')
    }
  }

  const handleVoteChange = useCallback((id: number, hasVoted: boolean, voteCount: number) => {
    setThemes((prev) =>
      prev.map((theme) => {
        const existing = theme.items?.find((item) => item.id === id)
        if (!existing || !theme.items) return theme
        const delta = voteCount - existing.vote_count
        return {
          ...theme,
          total_votes: theme.total_votes + delta,
          items: theme.items.map((item) =>
            item.id === id ? { ...item, has_voted: hasVoted, vote_count: voteCount } : item
          ),
        }
      })
    )
    setUngrouped((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, has_voted: hasVoted, vote_count: voteCount } : item
      )
    )
    onVoteChange?.(id, hasVoted, voteCount)
  }, [onVoteChange])

  async function handleAutoGroup() {
    setAutoThemeGenerating(true)
    setAutoThemeError(null)
    try {
      const result = await generateAutoThemes()
      if (result.themes.length === 0) {
        setAutoThemeError('All items appear too dissimilar to group automatically. Try adding more feedback items first.')
      } else {
        setAutoThemeProposals(result.themes)
        setAutoThemeUnclassifiable(result.unclassifiable_ids || [])
      }
    } catch (err) {
      const status = (err as Record<string, unknown>)?.status
      if (status === 503) {
        setAutoThemeError('AI features require the Gemini API key to be configured. Contact your administrator.')
      } else {
        setAutoThemeError('Failed to generate theme proposals. Please try again.')
      }
    } finally {
      setAutoThemeGenerating(false)
    }
  }

  function handleAutoThemeDone() {
    setAutoThemeProposals(null)
    setAutoThemeUnclassifiable([])
    fetchRef.current() // Refresh themes list
  }

  // If auto-theme proposals are active, show the review overlay
  if (autoThemeProposals) {
    return (
      <AutoThemeReview
        proposals={autoThemeProposals}
        unclassifiableIds={autoThemeUnclassifiable}
        ungroupedItems={ungrouped}
        totalUngrouped={ungrouped.length}
        onDone={handleAutoThemeDone}
        onItemSelect={onItemSelect}
      />
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (themes.length === 0 && ungrouped.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <FolderOpen className="w-12 h-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-semibold mb-1">No themes yet</h3>
        <p className="text-sm text-muted-foreground">
          Select feedback items in List view and group them into themes
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {/* Error banner (auto-theme generation + theme mutations) */}
      {autoThemeError && (
        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive mb-4 flex items-center justify-between">
          <span>{autoThemeError}</span>
          <button onClick={() => setAutoThemeError(null)} className="p-1 hover:bg-destructive/10 rounded">
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Themes */}
      {themes.map((theme) => (
        <div key={theme.id} className="border border-border rounded-lg overflow-hidden">
          {/* Theme header row */}
          <div
            className="flex items-center gap-2 px-4 py-3 bg-secondary/30 cursor-pointer hover:bg-secondary/50 transition-colors"
            onClick={() => toggleTheme(theme.id)}
          >
            {theme.expanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            )}

            <FolderOpen className="w-4 h-4 text-primary shrink-0" />

            {editingTheme === theme.id ? (
              <div className="flex-1 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEditTheme(theme.id)
                    if (e.key === 'Escape') setEditingTheme(null)
                  }}
                />
                <button
                  onClick={() => saveEditTheme(theme.id)}
                  className="p-1 rounded hover:bg-secondary"
                >
                  <Check className="w-4 h-4 text-green-600" />
                </button>
                <button
                  onClick={() => setEditingTheme(null)}
                  className="p-1 rounded hover:bg-secondary"
                >
                  <XIcon className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            ) : (
              <>
                <span
                  className="text-sm font-semibold flex-1 truncate hover:text-primary transition-colors"
                  onClick={(e) => { e.stopPropagation(); onThemeSelect?.(theme.id) }}
                  role="button"
                  tabIndex={0}
                >
                  {theme.name}
                </span>
                {theme.description && (
                  <span className="text-xs text-muted-foreground truncate max-w-[200px] hidden sm:inline">
                    {theme.description}
                  </span>
                )}
              </>
            )}

            <span className="text-xs text-muted-foreground shrink-0">
              {theme.item_count} item{theme.item_count !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-muted-foreground shrink-0">
              {theme.total_votes} vote{theme.total_votes !== 1 ? 's' : ''}
            </span>

            {isAdmin && editingTheme !== theme.id && (
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => startEditTheme(theme)}
                  className="p-1 rounded hover:bg-secondary transition-colors"
                  title="Edit theme"
                >
                  <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
                <button
                  onClick={() => handleDeleteTheme(theme.id)}
                  className="p-1 rounded hover:bg-red-500/10 transition-colors"
                  title="Delete theme"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </button>
              </div>
            )}
          </div>

          {/* Theme items */}
          {theme.expanded && theme.items && (
            <div className="divide-y divide-border bg-card">
              {theme.items.length === 0 ? (
                <div className="px-4 py-3 pl-12 text-xs text-muted-foreground italic">
                  No items in this theme
                </div>
              ) : (
                theme.items.map((item) => (
                  <FeedbackThemeItem
                    key={item.id}
                    item={item}
                    isAdmin={isAdmin}
                    onSelect={onItemSelect}
                    onRemove={() => handleRemoveFromTheme(theme.id, item.id)}
                    onVoteChange={handleVoteChange}
                  />
                ))
              )}
            </div>
          )}
        </div>
      ))}

      {/* Ungrouped section */}
      {ungrouped.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <div
            className="flex items-center gap-2 px-4 py-3 bg-secondary/20 cursor-pointer hover:bg-secondary/30 transition-colors"
            onClick={() => setUngroupedExpanded(!ungroupedExpanded)}
          >
            {ungroupedExpanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            )}
            <Inbox className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-semibold flex-1 text-muted-foreground">Ungrouped</span>
            <span className="text-xs text-muted-foreground shrink-0">
              {ungrouped.length} item{ungrouped.length !== 1 ? 's' : ''}
            </span>
          </div>

          {ungroupedExpanded && (
            <div className="divide-y divide-border bg-card">
              {ungrouped.map((item) => (
                <FeedbackThemeItem
                  key={item.id}
                  item={item}
                  isAdmin={isAdmin}
                  onSelect={onItemSelect}
                  onVoteChange={handleVoteChange}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Individual item row in the hierarchy
function FeedbackThemeItem({
  item,
  isAdmin,
  onSelect,
  onRemove,
  onVoteChange,
}: {
  item: FeedbackItem
  isAdmin: boolean
  onSelect: (id: number) => void
  onRemove?: () => void
  onVoteChange?: (id: number, hasVoted: boolean, voteCount: number) => void
}) {
  const typeBadge = item.type ? TYPE_BADGE[item.type] : null
  const displayStatus = item.status === 'dismissed' ? (item.previous_status || 'backlog') : item.status
  const statusBadge = STATUS_BADGE[displayStatus] || STATUS_BADGE.backlog
  const isDone = displayStatus === 'done'

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 pl-12 hover:bg-secondary/30 cursor-pointer transition-colors group ${
        isDone ? 'opacity-60' : ''
      }`}
      onClick={() => onSelect(item.id)}
    >
      {/* Vote */}
      <div onClick={(e) => e.stopPropagation()}>
        <FeedbackVoteButton
          feedbackId={item.id}
          initialHasVoted={item.has_voted}
          initialVoteCount={item.vote_count}
          onVoteChange={(hasVoted, voteCount) => onVoteChange?.(item.id, hasVoted, voteCount)}
        />
      </div>

      {/* Title */}
      <span className="flex items-center gap-1.5 flex-1 min-w-0">
        {isDone && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />}
        <span className={`text-sm font-medium truncate ${isDone ? 'line-through text-muted-foreground' : ''}`}>
          {item.title}
        </span>
      </span>

      {/* Badges */}
      {typeBadge && (
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full shrink-0 ${typeBadge.bg} ${typeBadge.text}`}>
          {typeBadge.label}
        </span>
      )}
      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full shrink-0 ${statusBadge.bg} ${statusBadge.text}`}>
        {statusBadge.label}
      </span>

      {/* Comments */}
      {item.comment_count > 0 && (
        <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
          <MessageCircle className="w-3 h-3" />
          {item.comment_count}
        </span>
      )}

      {/* Time */}
      <span className="text-xs text-muted-foreground shrink-0 hidden sm:inline">
        {formatRelativeTime(item.created_at)}
      </span>

      {/* Remove from theme button (admin only) */}
      {isAdmin && onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="p-1 rounded hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all shrink-0"
          title="Remove from theme"
        >
          <XIcon className="w-3.5 h-3.5 text-red-500" />
        </button>
      )}
    </div>
  )
}
